import { SunfireRow, normalizePhone } from './csvParser';

export interface AudioFile {
  file: File;
  phone: string;
  date: string;
}

export interface MatchedPair {
  csvRow: SunfireRow;
  audioFile: File;
  matchType: 'auto' | 'manual';
}

export interface MatchResult {
  matched: MatchedPair[];
  unmatchedCsv: SunfireRow[];
  unmatchedAudio: AudioFile[];
}

/**
 * Extract phone number and date from an audio filename.
 * Expected format: "3308034565_20260330.mp3" or similar with digits.
 */
export function parseAudioFilename(filename: string): { phone: string; date: string } {
  const base = filename.replace(/\.[^.]+$/, ''); // strip extension
  const digits = base.replace(/\D/g, '');

  let phone = '';
  let date = '';

  // Try to find a 10-digit phone number
  const phoneMatch = digits.match(/(\d{10})/);
  if (phoneMatch) {
    phone = phoneMatch[1];
    // Look for 8-digit date (YYYYMMDD or MMDDYYYY) in remaining digits
    const remaining = digits.replace(phone, '');
    const dateMatch = remaining.match(/(\d{8})/);
    if (dateMatch) {
      const d = dateMatch[1];
      // Determine format: if first 4 digits are a plausible year (2020-2030), it's YYYYMMDD
      const yearFirst = parseInt(d.slice(0, 4));
      if (yearFirst >= 2020 && yearFirst <= 2030) {
        date = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
      } else {
        // Try MMDDYYYY
        date = `${d.slice(4, 8)}-${d.slice(0, 2)}-${d.slice(2, 4)}`;
      }
    }
  }

  return { phone, date };
}

/**
 * Auto-match audio files to CSV rows by phone number + date.
 */
export function autoMatch(csvRows: SunfireRow[], audioFiles: AudioFile[]): MatchResult {
  const matched: MatchedPair[] = [];
  const usedCsvIndices = new Set<number>();
  const usedAudioIndices = new Set<number>();

  for (let ai = 0; ai < audioFiles.length; ai++) {
    const audio = audioFiles[ai];
    if (!audio.phone) continue;

    for (let ci = 0; ci < csvRows.length; ci++) {
      if (usedCsvIndices.has(ci)) continue;
      const row = csvRows[ci];

      const phoneMatch = normalizePhone(row.applicantPhone) === audio.phone;
      const dateMatch = !audio.date || row.date === audio.date;

      if (phoneMatch && dateMatch) {
        matched.push({ csvRow: row, audioFile: audio.file, matchType: 'auto' });
        usedCsvIndices.add(ci);
        usedAudioIndices.add(ai);
        break;
      }
    }
  }

  const unmatchedCsv = csvRows.filter((_, i) => !usedCsvIndices.has(i));
  const unmatchedAudio = audioFiles.filter((_, i) => !usedAudioIndices.has(i));

  return { matched, unmatchedCsv, unmatchedAudio };
}

/**
 * Upload a file to S3 via pre-signed URL with progress tracking.
 */
export function uploadToS3(
  url: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(true);
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('S3 upload network error'));
    xhr.send(file);
  });
}
